import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Logo, Brand } from "@/components/Logo";
import { ArrowLeft, Upload, FileText, Loader2, Download, Sparkles, Crown, History, Trash2, Plus } from "lucide-react";

export default function ResumeBuilder() {
  const navigate = useNavigate();
  const { user, isPro } = useAuth();
  const fileRef = useRef(null);
  const [resumeText, setResumeText] = useState("");
  const [jobDesc, setJobDesc] = useState("");
  const [notes, setNotes] = useState("");
  const [parsing, setParsing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null); // {resume, cover_letter}
  const [dl, setDl] = useState("");
  const [checkout, setCheckout] = useState(false);
  const [history, setHistory] = useState([]);

  const pro = isPro || user?.role === "owner" || user?.role === "admin";

  const loadHistory = async () => {
    try {
      const r = await api.get("/resume/history");
      setHistory(r.data);
    } catch { /* ignore */ }
  };

  useEffect(() => { if (pro) loadHistory(); }, [pro]);

  const openSaved = (item) => {
    setResult({ resume: item.resume, cover_letter: item.cover_letter });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const deleteSaved = async (id, e) => {
    e.stopPropagation();
    try {
      await api.delete(`/resume/history/${id}`);
      setHistory((h) => h.filter((x) => x.id !== id));
      toast.success("Removed");
    } catch { toast.error("Couldn't delete"); }
  };

  const upgrade = async () => {
    setCheckout(true);
    try {
      const { data } = await api.post("/payments/checkout", { origin_url: window.location.origin, plan: "monthly" });
      window.location.href = data.checkout_url;
    } catch {
      toast.error("Could not start checkout. Please try again.");
      setCheckout(false);
    }
  };

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setParsing(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const r = await api.post("/resume/parse", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setResumeText(r.data.text);
      toast.success("Resume loaded — edit or generate");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't read that file");
    } finally { setParsing(false); }
  };

  const generate = async () => {
    if (!resumeText.trim() && !jobDesc.trim()) { toast.error("Upload/paste your resume or add a target job"); return; }
    setLoading(true); setResult(null);
    try {
      const r = await api.post("/resume/generate", { resume_text: resumeText, job_description: jobDesc, notes });
      setResult(r.data);
      loadHistory();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't generate right now");
    } finally { setLoading(false); }
  };

  const download = async (content, filename, fmt) => {
    setDl(`${filename}-${fmt}`);
    try {
      const res = await fetch(`${API}/resume/download`, {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ content, filename, fmt }),
      });
      if (!res.ok) throw new Error();
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `${filename}.${fmt}`; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Download failed"); }
    finally { setDl(""); }
  };

  const inputCls = "w-full bg-[#0a0a0f] border border-[#2a3040] rounded-lg px-4 py-3 text-white placeholder:text-[#6b7280] focus:outline-none focus:border-[#FF5A00] transition-colors";

  return (
    <div className="min-h-screen bg-[#0a0a0f]" data-testid="resume-builder-page">
      <header className="sticky top-0 z-20 backdrop-blur-xl bg-[#0a0a0f]/80 border-b border-[#2a3040]" style={{ paddingTop: "env(safe-area-inset-top)" }}>
        <div className="max-w-4xl mx-auto px-5 h-16 flex items-center justify-between">
          <button data-testid="resume-back" onClick={() => navigate("/app")} className="flex items-center gap-2 text-sm text-[#9ca3af] hover:text-white transition-colors"><ArrowLeft size={18} /> Back</button>
          <Logo size="text-xl" />
          <div className="w-12" />
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-5 py-10">
        <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-[#FF5A00] font-bold mb-3"><FileText size={14} /> ATS Resume &amp; Cover Letter Builder</div>
        <h1 className="font-display font-black text-3xl sm:text-4xl tracking-tight text-white mb-2">Land the interview</h1>
        <p className="text-[#9ca3af] mb-8 max-w-xl">Upload your resume, tell us the job, and <Brand /> rewrites it into a polished, ATS-friendly resume and cover letter — download in Word or PDF.</p>

        {!pro ? (
          <div className="bg-[#15151d] border border-[#FF5A00]/40 rounded-2xl p-8 text-center" data-testid="resume-upsell">
            <Crown size={30} className="text-[#FF5A00] mx-auto mb-3" />
            <h2 className="font-display font-black text-2xl text-white mb-2">A Pro feature</h2>
            <p className="text-[#9ca3af] mb-5 max-w-md mx-auto">The Resume &amp; Cover Letter Builder is included with aiBLACK Pro. Upgrade to build unlimited tailored resumes.</p>
            <button data-testid="resume-upgrade" onClick={upgrade} disabled={checkout} className="inline-flex items-center gap-2 bg-[#FF5A00] text-[#0a0a0f] font-bold rounded-full px-6 py-3 hover:bg-[#FF7A33] transition-colors disabled:opacity-60">{checkout ? "Redirecting…" : "Upgrade to Pro"}</button>
          </div>
        ) : (
          <div className="space-y-4">
            <div>
              <input ref={fileRef} type="file" accept=".pdf,.docx,.txt" onChange={onFile} className="hidden" data-testid="resume-file-input" />
              <button data-testid="resume-upload-btn" onClick={() => fileRef.current?.click()} disabled={parsing}
                className="w-full flex items-center justify-center gap-2 border-2 border-dashed border-[#2a3040] hover:border-[#FF5A00] rounded-xl py-6 text-[#9ca3af] hover:text-white transition-colors">
                {parsing ? <Loader2 size={18} className="animate-spin" /> : <Upload size={18} />}
                {parsing ? "Reading your file…" : "Upload resume (PDF, Word, or .txt)"}
              </button>
            </div>
            <textarea data-testid="resume-text" className={inputCls} rows={7} placeholder="…or paste your current resume / experience here" value={resumeText} onChange={(e) => setResumeText(e.target.value)} />
            <textarea data-testid="resume-job" className={inputCls} rows={4} placeholder="Paste the target job description (optional but recommended)" value={jobDesc} onChange={(e) => setJobDesc(e.target.value)} />
            <input data-testid="resume-notes" className={inputCls} placeholder="Anything to emphasize? (optional)" value={notes} onChange={(e) => setNotes(e.target.value)} />
            <button data-testid="resume-generate" onClick={generate} disabled={loading}
              className="inline-flex items-center gap-2 bg-[#FF5A00] text-[#0a0a0f] font-bold rounded-lg px-6 py-3 hover:bg-[#FF7A33] transition-colors disabled:opacity-60">
              {loading ? <Loader2 size={16} className="animate-spin" /> : <Sparkles size={16} />} {loading ? "Building…" : "Generate resume & cover letter"}
            </button>

            {result && (
              <div className="space-y-6 pt-4" data-testid="resume-result">
                {[["Resume", result.resume, "aiBLACK-resume"], ["Cover Letter", result.cover_letter, "aiBLACK-cover-letter"]].map(([label, content, fn]) => content && (
                  <div key={label} className="bg-[#15151d] border border-[#2a3040] rounded-xl p-5">
                    <div className="flex items-center justify-between mb-3">
                      <h3 className="font-display font-bold text-xl text-white">{label}</h3>
                      <div className="flex items-center gap-2">
                        <button data-testid={`resume-download-${fn}`} onClick={() => download(content, fn, "docx")} disabled={dl === `${fn}-docx`}
                          className="inline-flex items-center gap-1.5 bg-[#1a1a24] border border-[#2a3040] text-white rounded-lg px-3 py-2 text-sm hover:border-[#FF5A00] transition-colors disabled:opacity-60">
                          {dl === `${fn}-docx` ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />} Word
                        </button>
                        <button data-testid={`resume-download-pdf-${fn}`} onClick={() => download(content, fn, "pdf")} disabled={dl === `${fn}-pdf`}
                          className="inline-flex items-center gap-1.5 bg-[#1a1a24] border border-[#2a3040] text-white rounded-lg px-3 py-2 text-sm hover:border-[#FF5A00] transition-colors disabled:opacity-60">
                          {dl === `${fn}-pdf` ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />} PDF
                        </button>
                      </div>
                    </div>
                    <pre className="whitespace-pre-wrap break-words text-[13px] text-[#c7ccd6] font-sans leading-relaxed">{content}</pre>
                  </div>
                ))}
              </div>
            )}

            {history.length > 0 && (
              <div className="pt-8 mt-4 border-t border-[#2a3040]" data-testid="resume-history">
                <div className="flex items-center gap-2 mb-4">
                  <History size={16} className="text-[#FF5A00]" />
                  <h3 className="font-display font-bold text-lg text-white">Saved resumes</h3>
                </div>
                <div className="space-y-2">
                  {history.map((item) => (
                    <button key={item.id} data-testid={`resume-history-item-${item.id}`} onClick={() => openSaved(item)}
                      className="w-full flex items-center justify-between gap-3 bg-[#15151d] border border-[#2a3040] hover:border-[#FF5A00] rounded-lg px-4 py-3 text-left transition-colors">
                      <div className="min-w-0">
                        <div className="text-sm font-semibold text-white truncate">{item.title}</div>
                        <div className="text-xs text-[#6b7280]">{new Date(item.created_at).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })}</div>
                      </div>
                      <span data-testid={`resume-history-delete-${item.id}`} onClick={(e) => deleteSaved(item.id, e)}
                        className="shrink-0 text-[#6b7280] hover:text-[#FF5A00] transition-colors p-1"><Trash2 size={16} /></span>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
